import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import { Checkbox } from '@ifixit/ui/components/checkbox';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { PrimaryButton } from 'Shared/editor/buttons';
import type { EditableItem } from './guide_patch';

/** A nested prerequisite offered for import, with its display bits. */
type ImportablePrereq = {
   guideid: number;
   title: string;
   imageUrl: string | null;
};

/**
 * What a just-picked prerequisite guide has to offer: its own prerequisites
 * and the tools/parts this guide doesn't list yet. Already filtered — every
 * entry here is genuinely addable.
 */
export type PrereqImportOffer = {
   /** The picked prerequisite the content comes from. */
   sourceTitle: string;
   prerequisites: ImportablePrereq[];
   tools: EditableItem[];
   parts: EditableItem[];
};

export type PrereqImportSelection = {
   prerequisites: ImportablePrereq[];
   tools: EditableItem[];
   parts: EditableItem[];
};

/**
 * The import offer shown right after a prerequisite is added, replacing v1's
 * post-add modal. Skip keeps just the already-added prerequisite. Selections
 * land in the form only; nothing persists until the page is saved.
 */
export function PrereqImportDialog({
   offer,
   onConfirm,
   onSkip,
}: {
   offer: PrereqImportOffer;
   onConfirm: (selection: PrereqImportSelection) => void;
   /** Close/Escape/Skip: add none of the offer (the prereq itself stays). */
   onSkip: () => void;
}) {
   // Deselections rather than selections, so "everything checked" needs no
   // state initialization from props.
   const [uncheckedPrereqs, setUncheckedPrereqs] = useState<ReadonlySet<number>>(new Set());
   const [uncheckedTools, setUncheckedTools] = useState<ReadonlySet<string>>(new Set());
   // Parts are the exception: a prerequisite's parts are rarely the ones this
   // guide needs, so they start off and you check the ones you want.
   const [uncheckedParts, setUncheckedParts] = useState<ReadonlySet<string>>(
      () => new Set(offer.parts.map(part => part.name))
   );

   const selection: PrereqImportSelection = {
      prerequisites: offer.prerequisites.filter(prereq => !uncheckedPrereqs.has(prereq.guideid)),
      tools: offer.tools.filter(tool => !uncheckedTools.has(tool.name)),
      parts: offer.parts.filter(part => !uncheckedParts.has(part.name)),
   };
   const selectedCount =
      selection.prerequisites.length + selection.tools.length + selection.parts.length;

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) {
               onSkip();
            }
         }}
      >
         <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
               {/* The close button is absolute; without this a full-width
                   first line runs under it. */}
               <DialogTitle className="pr-6">
                  {_js('Import from “%1”?', offer.sourceTitle)}
               </DialogTitle>
               <DialogDescription>
                  {_js(
                     'The prerequisite you just added has %1 of its own. Changes apply when you save the guide.',
                     offeredKindsPhrase(offer)
                  )}
               </DialogDescription>
            </DialogHeader>

            {offer.prerequisites.length > 0 && (
               <ImportSection
                  heading={_js('Prerequisite guides')}
                  hint={_js(
                     'Steps from these guides only appear in this guide if you list them as prerequisites too.'
                  )}
                  onSelectAll={() => setUncheckedPrereqs(new Set())}
                  onSelectNone={() =>
                     setUncheckedPrereqs(new Set(offer.prerequisites.map(prereq => prereq.guideid)))
                  }
               >
                  {offer.prerequisites.map(prereq => (
                     <ImportRow
                        key={prereq.guideid}
                        label={prereq.title}
                        imageUrl={prereq.imageUrl}
                        placeholderIcon="list-ol"
                        checked={!uncheckedPrereqs.has(prereq.guideid)}
                        onCheckedChange={checked =>
                           setUncheckedPrereqs(toggled(uncheckedPrereqs, prereq.guideid, checked))
                        }
                     />
                  ))}
               </ImportSection>
            )}

            {offer.tools.length > 0 && (
               <ImportSection
                  heading={_js('Tools')}
                  onSelectAll={() => setUncheckedTools(new Set())}
                  onSelectNone={() =>
                     setUncheckedTools(new Set(offer.tools.map(tool => tool.name)))
                  }
               >
                  {offer.tools.map(tool => (
                     <ImportRow
                        key={tool.name}
                        label={tool.name}
                        quantity={tool.quantity}
                        imageUrl={tool.imageUrl ?? null}
                        placeholderIcon="screwdriver-wrench"
                        checked={!uncheckedTools.has(tool.name)}
                        onCheckedChange={checked =>
                           setUncheckedTools(toggled(uncheckedTools, tool.name, checked))
                        }
                     />
                  ))}
               </ImportSection>
            )}

            {offer.parts.length > 0 && (
               <ImportSection
                  heading={_js('Parts')}
                  onSelectAll={() => setUncheckedParts(new Set())}
                  onSelectNone={() =>
                     setUncheckedParts(new Set(offer.parts.map(part => part.name)))
                  }
               >
                  {offer.parts.map(part => (
                     <ImportRow
                        key={part.name}
                        label={part.name}
                        quantity={part.quantity}
                        imageUrl={part.imageUrl ?? null}
                        placeholderIcon="box"
                        checked={!uncheckedParts.has(part.name)}
                        onCheckedChange={checked =>
                           setUncheckedParts(toggled(uncheckedParts, part.name, checked))
                        }
                     />
                  ))}
               </ImportSection>
            )}

            <DialogFooter>
               <Button type="button" className="button-md button-outline" onClick={onSkip}>
                  {_js('Skip')}
               </Button>
               <PrimaryButton disabled={selectedCount === 0} onClick={() => onConfirm(selection)}>
                  {_js('Add selected')}
               </PrimaryButton>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
}

/**
 * What the offer holds, as a phrase for the description: "guides", "tools
 * and parts", "guides, tools, and parts", … in section order. Whole joined
 * forms (not a comma join) so translators control the list grammar.
 */
function offeredKindsPhrase(offer: PrereqImportOffer): string {
   const kinds: string[] = [];
   if (offer.prerequisites.length > 0) {
      kinds.push(_js('guides'));
   }
   if (offer.tools.length > 0) {
      kinds.push(_js('tools'));
   }
   if (offer.parts.length > 0) {
      kinds.push(_js('parts'));
   }
   if (kinds.length === 3) {
      return _js('%1, %2, and %3', kinds[0], kinds[1], kinds[2]);
   }
   if (kinds.length === 2) {
      return _js('%1 and %2', kinds[0], kinds[1]);
   }
   return kinds[0] ?? '';
}

function toggled<Key>(set: ReadonlySet<Key>, key: Key, checked: boolean | 'indeterminate') {
   const next = new Set(set);
   if (checked === true) {
      next.delete(key);
   } else {
      next.add(key);
   }
   return next;
}

function ImportSection({
   heading,
   hint,
   onSelectAll,
   onSelectNone,
   children,
}: {
   heading: string;
   hint?: string;
   onSelectAll: () => void;
   onSelectNone: () => void;
   children: ReactNode;
}) {
   return (
      // A named group so the per-section select-all/none buttons (visually
      // just "Select all | none") stay distinguishable to screen readers and
      // tests via their section.
      <div role="group" aria-label={heading}>
         <div className="mb-1 flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{heading}</h3>
            <span className="shrink-0 text-xs text-gray-400">
               <SelectSpanButton label={_js('Select all')} onClick={onSelectAll} />
               <span aria-hidden> | </span>
               <SelectSpanButton label={_js('none')} onClick={onSelectNone} />
            </span>
         </div>
         {hint && <p className="mb-2 text-xs text-gray-500">{hint}</p>}
         <div className="flex flex-col gap-1">{children}</div>
      </div>
   );
}

/** A quiet text button for the section-level select all/none. */
function SelectSpanButton({ label, onClick }: { label: string; onClick: () => void }) {
   return (
      <button
         type="button"
         onClick={onClick}
         className="rounded text-gray-500 outline-none hover:text-brand-600 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
         {label}
      </button>
   );
}

/**
 * One checkbox row of the offer. The whole row is the label, so the small
 * checkbox isn't the only click target.
 */
function ImportRow({
   label,
   quantity,
   imageUrl,
   placeholderIcon,
   checked,
   onCheckedChange,
}: {
   label: string;
   /** Item quantity; shown only when it says more than "one". */
   quantity?: number;
   imageUrl: string | null;
   placeholderIcon: string;
   checked: boolean;
   onCheckedChange: (checked: boolean | 'indeterminate') => void;
}) {
   return (
      <label
         className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md border p-1.5',
            !checked && 'opacity-60'
         )}
      >
         <Checkbox
            className="ml-1 size-4 shrink-0"
            checked={checked}
            onCheckedChange={onCheckedChange}
         />
         {imageUrl ? (
            <img src={imageUrl} alt="" className="size-9 shrink-0 rounded object-cover" />
         ) : (
            <span className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-400">
               <FaIcon name={placeholderIcon} className="size-4" />
            </span>
         )}
         <span className="line-clamp-2 min-w-0 flex-1 text-sm text-gray-900">{label}</span>
         {quantity !== undefined && quantity > 1 && (
            <span className="shrink-0 text-xs tabular-nums text-gray-500">
               {_js('× %1', String(quantity))}
            </span>
         )}
      </label>
   );
}
